
import { memo, useCallback, useMemo, useState } from 'react';

import { Button, Confirm, Modal } from '@components/common';
import CollapsibleActionsMenu from '@components/common/CollapsibleActionsMenu';

import { useMutateConsistencyCheckRanDisableManyMonitoring } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanDisableManyMonitoring';
import { useMutateConsistencyCheckRanEnableManyMonitoring } from '@hooks/cmModule/consistencyCheckRan/useMutateConsistencyCheckRanEnableManyMonitoring';

import { IConsistencyCheckActions } from '@interfaces/cmModule/consistencyCheck';
import { TConsistencyCheckRanGetAvailableTemplatesDTO } from '@interfaces/cmModule/dto/consistencyCheckDTO';

import { MODAL_WIDTH_480 } from '@constants/modals';

import { getAssignConsistencyCheckOptions, getMonitoringParams } from './utils';

import styles from './ChangeMonitoringConsistencyCheckMenu.module.scss';

interface IChangeMonitoringConsistencyCheckMenu extends IConsistencyCheckActions {
	isVisible: boolean;
	toggleVisibility: () => void;
}

const ChangeMonitoringConsistencyCheckMenu = ({
	currentPageIds,
	selectedIds,
	filter,
	isVisible,
	toggleVisibility,
}: IChangeMonitoringConsistencyCheckMenu) => {
	const [filterParams, setFilterParams] = useState<TConsistencyCheckRanGetAvailableTemplatesDTO | undefined>();
	const [isOpenConfirm, setIsOpenConfirm] = useState(false);
	const [isDisableMode, setIsDisableMode] = useState(true);

	const isEmptyFilter = useMemo(() => Object.values(filter).every(item => !item), [filter]);

	const openConfirm = useCallback(() => setIsOpenConfirm(true), [setIsOpenConfirm]);
	const closeConfirm = useCallback(() => setIsOpenConfirm(false), [setIsOpenConfirm]);

	const {
		disableManyMonitoring,
		isLoadingDisableManyMonitoring,
	} = useMutateConsistencyCheckRanDisableManyMonitoring({
		onSuccess: closeConfirm,
	});

	const {
		enableManyMonitoring,
		isLoadingEnableManyMonitoring,
	} = useMutateConsistencyCheckRanEnableManyMonitoring({
		onSuccess: closeConfirm,
	});

	const handleMonitoringChange = useCallback(() => {
		if (!filterParams) {
			return;
		}

		if (isDisableMode) {
			disableManyMonitoring(filterParams);
		} else {
			enableManyMonitoring(filterParams);
		}
	}, [
		filterParams,
		isDisableMode,
		enableManyMonitoring,
		disableManyMonitoring,
	]);

	const onOptionClick = useCallback((actionId: string) => {
		setFilterParams(getMonitoringParams({
			actionId,
			currentPageIds,
			selectedIds,
			filter,
			setIsDisableMode,
		}));

		openConfirm();
	}, [
		currentPageIds,
		selectedIds,
		filter,
		setIsDisableMode,
		openConfirm,
	]);

	const options = useMemo(() => getAssignConsistencyCheckOptions(
		selectedIds.length !== 0,
		isEmptyFilter,
	), [selectedIds, isEmptyFilter]);

	const isLoading = isLoadingDisableManyMonitoring || isLoadingEnableManyMonitoring;

	return (
		<div className={styles.container} >
			<Modal
				isLoading={isLoading}
				isOpen={isOpenConfirm}
				onClose={closeConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeConfirm}
					onSubmit={handleMonitoringChange}
					question={isDisableMode
						? 'Are you sure you want to disable monitoring?'
						: 'Are you sure you want to enable monitoring?'
					}
				/>
			</Modal>
			<Button
				onClick={toggleVisibility}
				secondary
				text='Manage monitoring'
			/>
			<CollapsibleActionsMenu
				actions={options}
				isVisibleMenu={isVisible}
				onClick={onOptionClick}
			/>
		</div>
	);
};

export default memo(ChangeMonitoringConsistencyCheckMenu);
